import { useMemo } from "react";

import { Trans } from "@lingui/react/macro";
import { apiFetch } from "@pontimed/api-client";
import { cn } from "@pontimed/ui";
import { useSession } from "@pontimed/auth-client";
import { useQuery } from "@tanstack/react-query";
import { Link, createFileRoute } from "@tanstack/react-router";
import { ArrowRight, Check, Users } from "lucide-react";

import { AccountSecurityBanner } from "@/components/account/account-security-banner";
import { ProviderTeamClient } from "@/components/account/provider-team-client";
import {
  FeatureAccessProvider,
  useFeatureAccess,
  useFeatureAccessLoading,
} from "@/components/features/feature-access-provider";
import { PageHeader } from "@/components/layout/page-header";
import { buttonVariants } from "@/components/ui/button-variants";
import { Skeleton } from "@/components/ui/skeleton";
import type { OwnerType } from "@/lib/features/types";

export const Route = createFileRoute("/_authenticated/account/provider/team")({
  component: ProviderTeamRoute,
});

// The workspace guard (`../provider/route.tsx`) now admits an accepted team
// seat and a superadmin as well as an own `providerId`, so staff-only members
// reach this page. What they see here still comes from `settings-data`'s
// `providerId`, i.e. the user's OWN practice: a seat holder with no provider
// profile of their own gets the empty state rather than the roster of the
// practice that invited them. Rendering someone else's roster needs the
// workspace switcher to name WHICH practice is active
// (/api/account/workspaces), which is a separate change.

const SETTINGS_KEY = ["api", "/api/account/user/settings-data"] as const;
const TEAM_KEY = ["api", "/api/account/provider-team"] as const;

interface SettingsDataLite {
  providerId: string | null;
}

interface TeamMemberLite {
  id: string;
  role: string;
  user: { id: string };
}

// Stable reference — feeds FeatureAccessProvider's memoized scope key.
const TEAM_PAGE_SCOPES: OwnerType[] = ["user", "provider"];

function ProviderTeamRoute() {
  // Shares the header/workspace-guard query cache — usually warm.
  const settingsQ = useQuery({
    queryKey: SETTINGS_KEY,
    queryFn: () => apiFetch.get<SettingsDataLite>("/api/account/user/settings-data"),
  });
  const ownProviderId = settingsQ.data?.providerId ?? null;

  const ownerIds = useMemo(
    () => (ownProviderId ? { provider: ownProviderId } : undefined),
    [ownProviderId],
  );

  // Legacy wrapped this page in useFeatureAccess("provider.team.staff_invites")
  // + FeatureTeaserPage (legacy provider-team-page-client.tsx). The SPA's
  // FeatureAccessProvider is not mounted globally yet, so mount it locally
  // around the gated content. Backend still enforces the real permission
  // check on every request — this is UX gating only.
  return (
    <FeatureAccessProvider activeScopes={TEAM_PAGE_SCOPES} ownerIds={ownerIds}>
      <GatedProviderTeam ownProviderId={ownProviderId} />
      <AccountSecurityBanner />
    </FeatureAccessProvider>
  );
}

function GatedProviderTeam({ ownProviderId }: { ownProviderId: string | null }) {
  const { user } = useSession();
  const access = useFeatureAccess("provider.team.staff_invites");
  const loading = useFeatureAccessLoading();

  // Same endpoint ProviderTeamClient fetches internally (react-query here vs
  // its local state) — used only to resolve the caller's own team role. The
  // legacy page read it from `session.user.providerTeams`, a claim the
  // apps/api JWT does not carry.
  const teamQ = useQuery({
    queryKey: TEAM_KEY,
    queryFn: () => apiFetch.get<{ members: TeamMemberLite[] }>("/api/account/provider-team"),
    staleTime: 30_000,
    retry: false,
  });

  if (loading || !access || teamQ.isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-48 w-full rounded-xl" />
      </div>
    );
  }

  const allowed =
    access.access === "included" || (access.access === "limited" && !access.exhausted);

  if (!allowed) {
    // Same page header as the team list, so the page keeps its h1 (P27).
    return (
      <div className="space-y-6">
        <PageHeader
          title={<Trans>Team</Trans>}
          description={<Trans>Practice managers and assistants who help run your account.</Trans>}
        />
        <TeamFeatureTeaser />
      </div>
    );
  }

  // Real role from the team list (members include the current user); fall
  // back to "owner" ONLY when the caller owns this provider profile.
  const memberRole = teamQ.data?.members.find((m) => m.user.id === user?.userId)?.role;
  const currentUserRole = memberRole ?? (ownProviderId ? "owner" : "");

  return <ProviderTeamClient currentUserRole={currentUserRole} />;
}

// ─── Feature teaser (plan without staff invites) ────────────────────────────
//
// Inline teaser card following the apps/app pattern (see api-keys-client.tsx)
// — content mirrors the legacy FeatureTeaserPage copy for this page.

function TeamFeatureTeaser() {
  return (
    <div className="border-primary/20 bg-primary/5 rounded-xl border p-6">
      <div className="flex items-start gap-3">
        <div className="bg-primary/10 flex size-10 shrink-0 items-center justify-center rounded-lg">
          <Users className="text-primary size-5" />
        </div>
        <div className="flex-1">
          <h3 className="text-foreground text-base font-semibold">
            <Trans>Your team — staff with 4 roles</Trans>
          </h3>
          <p className="text-muted-foreground mt-1 text-sm">
            <Trans>This feature is not part of your current plan.</Trans>
          </p>
          <ul className="mt-3 space-y-1.5">
            {[
              <Trans key="b1">Invite office manager, assistant, receptionist, accountant</Trans>,
              <Trans key="b2">
                Each role has granular permissions on bookings, billing, patients
              </Trans>,
              <Trans key="b3">Staff activity is recorded in the audit log</Trans>,
              <Trans key="b4">Deactivate a staff account in 1 click when someone leaves</Trans>,
            ].map((label, i) => (
              <li key={i} className="text-muted-foreground flex items-center gap-2 text-xs">
                <Check className="text-primary size-3.5 shrink-0" />
                {label}
              </li>
            ))}
          </ul>
          <Link
            to="/account/user/billing"
            search={{ tab: "subscription", category: "provider" }}
            className={cn(buttonVariants({ size: "touch" }), "mt-4")}
          >
            <Trans>Upgrade plan</Trans>
            <ArrowRight className="size-4" aria-hidden />
          </Link>
        </div>
      </div>
    </div>
  );
}
